import InnerHeader from "component/shared/innerheader/InnerHeader";
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import ChevronIcon from "svg-icons/chevron-icon";
import TableData from "./components/TableData";
import Switch from "@mui/material/Switch";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import StackedLineChartOutlinedIcon from "@mui/icons-material/StackedLineChartOutlined";
import useExportToPDF from "component/htmlToPDF/export-html-pdf";
import ChartExportActions from "component/shared/ChartExportActions/ChartExportActions";
import { downloadElementAsImage } from "utils/chartExport";
import LineData from "./components/LineData";
import useSWR from "swr";
import { END_POINTS } from "constants/APIConstant";
import LabelLang, { LabelLangString } from "component/LabelLang/label-lang";
import DataNotFound from "component/shared/DataNotFound/DataNotFound";
import HtmlView from "component/htmlView/html-view";
import TableExportActions from "component/shared/TableExportActions/TableExportActions";
import { PeriodicTypeChecking } from "./components/functions";
import { PUBLICATIONS_PERIODIC_TYPES } from "constants/PublicationConstants";
import _ from "lodash";
import ContentLoading from "component/LoadingComponent/ContentLoading";
import ReactGA from "react-ga4";

function DataVisualization() {
  const intl = useIntl();
  const indicatorRefs = useRef([]);

  const excelImage = process.env.PUBLIC_URL + "/image/Excel.svg";

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, []);

  const [exportToPDF] = useExportToPDF();
  const elementPdf = useRef(null);

  const currentLocale = useSelector(({ theme }) => theme.locale);

  const dir = useSelector(({ theme }) => theme.direction);

  const [show, setShow] = useState(true);

  const { id, subId, indicatorId } = useParams();

  const [mainId, setMainId] = useState(id);
  const [indicatorDetailsValid, setIndicatorDetailsValid] = useState(false);
  const [subSubjectId, setSubSubjectId] = useState(subId);
  const [indicatorIdData, setIndicatorIdData] = useState(indicatorId);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [allChartData, setAllChartData] = useState([]);

  const mainList = useSWR({
    url: END_POINTS.MAIN_SUBJECTS,
  });

  const subSubjectsListWithMainId = useSWR({
    url: `${END_POINTS.SUBJECT_VISUALIZATION}/${mainId}`,
  });

  useEffect(() => {
    setIndicatorIdData(indicatorId);
  }, [indicatorId]);

  const indicatorsList = useSWR({
    url: `${END_POINTS.SUBJECT_DETAILS}/${subSubjectId}`,
  });

  const renderIndicatorList = () => {
    const publicationIndicators =
      indicatorsList.error ||
      indicatorsList.isLoading ||
      indicatorsList.data === undefined ||
      indicatorsList.data.data === undefined
        ? []
        : indicatorsList.data.data.publicationWithIndicators;

    const render =
      Object.keys(publicationIndicators).length > 0
        ? publicationIndicators.map((el) => {
            return el?.indicators;
          })
        : "";

    return render.length > 0 ? render.flat() : render;
  };

  const getSelectedIndicator = () => {
    return (
      renderIndicatorList()?.length > 0 &&
      renderIndicatorList()?.find((x) => x.indicatorId === +indicatorIdData)
    );
  };

  // Google Analytics
  ReactGA.initialize(END_POINTS.TRACKING_ID);

  useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: `/data/mainSubject/:id/subSubject/:subId/data-visualization/:indicatorId`,
      title: `Main Subject: ${
        indicatorsList?.data?.data?.mainSubject?.title
      } - Sub Subject: ${indicatorsList?.data?.data?.title} - Indicator: ${
        getSelectedIndicator()?.name
      }`,
    });
  }, [id, subId, getSelectedIndicator()?.name]);

  const [defaultVisualData, setDefaultVisualData] = useState({
    indicatorId: indicatorIdData,
    publicationId: null,
  });

  const getIndicatorPublicationId = (indicatorId) => {
    if (indicatorId) {
      const publications =
        indicatorsList.error ||
        indicatorsList.isLoading ||
        indicatorsList.data === undefined ||
        indicatorsList.data.data === undefined
          ? []
          : indicatorsList.data.data.publicationWithIndicators;
      for (const publication of publications) {
        const indicator = publication.indicators.find(
          (indicator) => indicator.indicatorId === +indicatorId
        );
        if (indicator) {
          return publication.id;
        }
      }
    }

    return null;
  };

  const getIndicatorPublication = (indicatorId) => {
    if (indicatorId) {
      const publications =
        indicatorsList.error ||
        indicatorsList.isLoading ||
        indicatorsList.data === undefined ||
        indicatorsList.data.data === undefined
          ? []
          : indicatorsList.data.data.publicationWithIndicators;
      for (const publication of publications) {
        const indicator = publication.indicators.find(
          (indicator) => indicator.indicatorId === +indicatorId
        );
        if (indicator) {
          return publication;
        }
      }
    }

    return null;
  };

  useLayoutEffect(() => {
    if (
      indicatorIdData !== null &&
      indicatorRefs.current[indicatorIdData] &&
      !indicatorsList.error &&
      !indicatorsList.isLoading &&
      indicatorsList.data !== undefined &&
      indicatorsList.data.data !== undefined
    ) {
      indicatorRefs.current[indicatorIdData].scrollIntoView({
        behavior: indicatorIdData === indicatorId ? "instant" : "smooth",
        block: "nearest",
      });
    }
  }, [indicatorIdData, indicatorsList]);

  useEffect(() => {
    if (
      indicatorIdData &&
      indicatorIdData !== undefined &&
      indicatorsList?.data?.data
    ) {
      let publicationId = getIndicatorPublicationId(indicatorIdData);

      if (publicationId != null) {
        setDefaultVisualData({
          indicatorId: indicatorIdData,
          publicationId: publicationId,
        });
      }
    }

  }, [indicatorIdData, indicatorsList?.data?.data]);

  useEffect(() => {
    if (
      defaultVisualData.indicatorId !== null &&
      defaultVisualData.publicationId !== null &&
      defaultVisualData.indicatorId !== undefined &&
      defaultVisualData.publicationId !== undefined
    ) {
      setIndicatorDetailsValid(true);
    } else {
      setIndicatorDetailsValid(false);
    }
  }, [defaultVisualData.indicatorId, defaultVisualData.publicationId]);

  const visualizationDetails = useSWR({
    url:
      indicatorDetailsValid && defaultVisualData.indicatorId
        ? END_POINTS.VISUALIZATION_DETAILS
        : null,
    params: indicatorDetailsValid ? defaultVisualData : null,
  });

  const defaultState = {
    IndicatorId: indicatorIdData,
    SubSubjectId: subSubjectId,
  };

  const [filters, setFilters] = useState(defaultState);

  const indicatorData = useSWR({
    url: END_POINTS.INDICATOR_FILTER,
    params: filters,
  });

  const getChecked = (id) => {
    return selectedIndicators.find((x) => x.id == id) != null;
  };

  function handleCheckedChange(id, checked) {
    let allSelectedBefore = selectedIndicators;
    let indicator = listOfCheckBox.find((x) => x.id == id);
    if (indicator != null) {
      if (checked) {
        let found = selectedIndicators?.find((x) => x.id == id);
        if (found == null) {
          setSelectedIndicators([...allSelectedBefore, indicator]);
        }
      } else {
        let item = selectedIndicators.find((x) => x.id == id);
        if (item != null) {
          setSelectedIndicators(selectedIndicators.filter((x) => x.id != id));
        }
      }
    }
  }

  useEffect(() => {
    // Only proceed if indicatorData is loaded
    if (!indicatorData?.data?.data) {
      setAllChartData([]);
      return;
    }

    if (selectedIndicators.length > 0) {
      let selectedIds = selectedIndicators.map((x) => x.id);
      setAllChartData(
        indicatorData?.data?.data
          ?.filter((item) => selectedIds.indexOf(item.id) !== -1)
          .map((el) => {
            const listOfContent = el?.data?.map((x) => {
              const monthId =
                x.month != null
                  ? parseInt(x.month) < 10
                    ? "0" + x.month
                    : x.month
                  : 0;
              const quarterId = x.quarter ?? 0;
              const month = PeriodicTypeChecking({
                periodicId: visualizationDetails?.data?.data?.periodic?.id,
                value: x.month,
                type: PUBLICATIONS_PERIODIC_TYPES.MONTH,
              });
              const quarter = PeriodicTypeChecking({
                periodicId: visualizationDetails?.data?.data?.periodic?.id,
                value: x.quarter,
                type: PUBLICATIONS_PERIODIC_TYPES.QUARTER,
              });
              return {
                id: el.id,
                name: el.name,
                quarter:
                  quarter !== ""
                    ? intl.formatMessage({ id: quarter })
                    : quarter,
                month: month !== "" ? intl.formatMessage({ id: month }) : month,
                year: x.year,
                value: x.value,
                monthId: monthId,
                quarterId: quarterId,
                translations: el.filterTranslations,
              };
            });
            return listOfContent;
          })
      );
    } else {
      // Clear chart data when all indicators are unchecked
      setAllChartData([]);
    }
  }, [
    selectedIndicators,
    currentLocale,
    indicatorData?.data?.data,
    visualizationDetails?.data?.data?.periodic?.id,
    intl,
  ]);
  const sortAscHeader = (arr) => {
    return arr.sort((a, b) => parseInt(a.sortingKey) - parseInt(b.sortingKey));
  };

  const getListOfCheckBox = () => {
    if (indicatorData?.data?.data) {
      const excludedArFilters = [
        "إجمالي",
        "اجمالي",
        "إجمالى",
        "اجمالى",
        "الرقم العام",
      ];
      const excludedArExactFilters = ["عام", "جملة"];
      const unfilteredData = indicatorData?.data?.data?.filter(
        (item) =>
          excludedArFilters.some((name) => item.name.includes(name)) ||
          excludedArExactFilters.some((name) => item.name === name)
      );
      const filteredData = indicatorData?.data?.data?.filter(
        (item) =>
          !excludedArFilters.some((name) => item.name.includes(name)) &&
          !excludedArExactFilters.some((name) => item.name === name)
      );
      const sortedFilteredData = filteredData.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      const sortedData = unfilteredData.concat(sortedFilteredData);

      return sortedData.map((x, index) => {
        return {
          id: x.id,
          name: x.name,
          translations: x.filterTranslations,
        };
      });
    }
    return [];
  };
  const listOfCheckBox =
    indicatorData?.data?.data &&
    indicatorData?.data?.data?.map((x, index) => {
      return {
        id: x.id,
        name: x.name,
        translations: x.filterTranslations,
      };
    });

  const handleDirFixedBar = () => {
    if (dir === "rtl") {
      document.querySelector(".fixed-sidebar").classList.toggle("r-active");
    } else {
      document.querySelector(".fixed-sidebar").classList.toggle("l-active");
    }
  };

  const handleDirContentFixedBar = () => {
    document
      .querySelector(".content-data-visualization .box-content")
      .classList.toggle("active");
    document
      .querySelector(".fixed-sidebar-box-content")
      .classList.toggle("active");
  };

  let columnsTable = [];

  for (let index = 0; index < allChartData.length; index++) {
    columnsTable.push(
      allChartData[index].map((item) => {
        return {
          headerName: item.year,
          row: item.value,
        };
      })
    );
  }
  const getColHeader = () => {
    let preparingData = [];
    for (let index = 0; index < allChartData.length; index++) {
      preparingData.push(
        allChartData[index].map((item) => {
          return {
            headerName:
              item.month + " " + item.quarter + " " + item.year.toString(),
            sortingKey:
              item.year.toString() +
              item.monthId.toString() +
              item.quarterId.toString(),
          };
        })
      );
    }
    const setColHeader = preparingData.flat();
    const filteredColHeader = _.uniqBy(setColHeader, (x) => x.headerName);

    const sortedFilteredColHeader = sortAscHeader(filteredColHeader);

    return sortedFilteredColHeader.length > 0
      ? [
          {
            headerName: intl.formatMessage({
              id: "indicator",
            }),
            isIndicatorName: true,
          },
          ...filteredColHeader,
        ]
      : [];
  };
  const resetChartsData = () => {
    setAllChartData([]);
    setSelectedIndicators([]);
  };

  const tableExportFilename =
    visualizationDetails?.data?.data?.name
      ? currentLocale === "ar"
        ? visualizationDetails?.data?.data?.name
        : visualizationDetails?.data?.data?.indicatorTranslations?.[0]?.name
      : "excel-sheet";

  const tableExportTitle = LabelLangString({
    currentLocale: currentLocale,
    id: "name",
    defaultValue: visualizationDetails?.data?.data?.name,
    translations: visualizationDetails?.data?.data?.indicatorTranslations,
  });

  const hasExportableData = allChartData.length > 0;

  const getChartImageFilename = (extension) => {
    if (visualizationDetails?.data?.data?.name) {
      return currentLocale === "ar"
        ? `الرسم البياني ل${visualizationDetails.data.data.name}.${extension}`
        : `chart-${visualizationDetails.data.data.indicatorTranslations?.[0]?.name}.${extension}`;
    }
    return `chart.${extension}`;
  };

  const handleChartPdfExport = () => {
    if (!hasExportableData) return;

    exportToPDF({
      ref: elementPdf,
      fileName: tableExportTitle,
      headerTitle: tableExportTitle,
    });
  };

  const handleChartPngExport = () => {
    if (!hasExportableData) return;

    downloadElementAsImage(
      elementPdf.current,
      getChartImageFilename("png"),
      "png"
    );
  };

  const handleChartJpegExport = () => {
    if (!hasExportableData) return;

    downloadElementAsImage(
      elementPdf.current,
      getChartImageFilename("jpg"),
      "jpeg"
    );
  };

  return (
    <ContentLoading
      isLoading={
        mainList.isLoading ||
        subSubjectsListWithMainId.isLoading
      }
    >
      <div className="data-visualization">
        <InnerHeader
          breadcrumb={[
            {
              title: intl.formatMessage({ id: "data" }),
              clickClass: false,
            },
            {
              title: intl.formatMessage({ id: "data-visualization" }),
              clickClass: true,
            },
          ]}
          title={intl.formatMessage({ id: "data-title" })}
          route={"../../data"}
        />
        <div className="container mt-4 mb-5 position-relative overflow-hidden">
          <div className={`fixed-sidebar ${dir === "rtl" ? "r-dir" : "l-dir"}`}>
            <div className="show-icon" onClick={handleDirFixedBar}>
              <Button className={`${dir === "rtl" ? "r-dir" : "l-dir"} `}>
                <ChevronIcon />
              </Button>
            </div>
            <div
              className={`main-button ${dir === "rtl" ? "r-line" : "l-line"}`}
            >
              <select
                name={`main-subject`}
                id="main-subject"
                onChange={(e) => {
                  setMainId(e.target.value);
                  setFilters({});
                  setDefaultVisualData({});
                  resetChartsData();
                  setSubSubjectId(subSubjectsListWithMainId?.data?.data[0]?.id);
                }}
                defaultValue={mainId}
              >
                {mainList?.data?.data &&
                  mainList?.data?.data.map((el, i) => (
                    <option
                      key={i}
                      value={el.id}
                      defaultChecked={el.id === mainId}
                      title={LabelLangString({
                        id: "name",
                        currentLocale: currentLocale,
                        defaultValue: el.name,
                        translations: el.translations,
                      })}
                    >
                      {LabelLangString({
                        id: "name",
                        currentLocale: currentLocale,
                        defaultValue: el.name,
                        translations: el.translations,
                      })}
                    </option>
                  ))}
              </select>
            </div>
            <div
              className={`second-button ${dir === "rtl" ? "r-line" : "l-line"}`}
            >
              {subSubjectsListWithMainId?.data?.data &&
              Object.keys(subSubjectsListWithMainId?.data?.data).length > 0 ? (
                <select
                  name="second-subject"
                  id="second-subject"
                  onChange={(e) => {
                    setSubSubjectId(e.target.value);
                    setIndicatorIdData(1);
                    setFilters({
                      ...defaultState,
                      SubSubjectId: e.target.value,
                    });
                    setDefaultVisualData({});
                    resetChartsData();
                  }}
                  defaultValue={subSubjectId}
                >
                  {subSubjectsListWithMainId?.data?.data &&
                    subSubjectsListWithMainId?.data?.data.map((el, i) => (
                      <option
                        key={i}
                        value={el.id}
                        defaultChecked={el.id === subSubjectId}
                        title={LabelLangString({
                          id: "title",
                          currentLocale: currentLocale,
                          defaultValue: el.title,
                          translations: el.subjectTranslations,
                        })}
                      >
                        {LabelLangString({
                          id: "title",
                          currentLocale: currentLocale,
                          defaultValue: el.title,
                          translations: el.subjectTranslations,
                        })}
                      </option>
                    ))}
                </select>
              ) : (
                <button>{intl.formatMessage({ id: "data-not-found" })}</button>
              )}
            </div>
            <form
              className="radio-box"
              onChange={(e) => {
                setIndicatorIdData(e.target.value);
                setFilters({ ...defaultState, IndicatorId: e.target.value });
                setDefaultVisualData({
                  ...defaultVisualData,
                  indicatorId: e.target.value,
                });
                resetChartsData();
              }}
            >
              {indicatorsList.isLoading || indicatorsList.error ? (
                <div className="text-center" dir="ltr">
                  <h5>{intl.formatMessage({ id: "loading..." })}</h5>
                </div>
              ) : Object.keys(renderIndicatorList()).length > 0 ? (
                <>
                  {renderIndicatorList()
                    .map((el, i) => (
                      <div
                        key={el.indicatorId}
                        ref={(element) =>
                          (indicatorRefs.current[el?.indicatorId] = element)
                        }
                        className={`item ${
                          dir === "rtl" ? "r-line" : "l-line"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`radio-${el.name}-${el.indicatorId}`}
                          id={`radio-${el.name}-${el.indicatorId}`}
                          value={el.indicatorId}
                          checked={+indicatorIdData === el.indicatorId}
                          onChange={() => setIndicatorIdData(el.indicatorId)}
                        />
                        <label
                          htmlFor={`radio-${el.name}-${el.indicatorId}`}
                          title={LabelLangString({
                            id: "name",
                            currentLocale: currentLocale,
                            defaultValue: el.name,
                            translations: el.indicatorTranslations,
                          })}
                        >
                          {LabelLangString({
                            id: "name",
                            currentLocale: currentLocale,
                            defaultValue: el.name,
                            translations: el.indicatorTranslations,
                          })}
                        </label>
                      </div>
                    ))}
                </>
              ) : (
                <div className={`mt-3 ${dir === "rtl" ? "me-3" : "ms-3"}`}>
                  <DataNotFound />
                </div>
              )}
            </form>
          </div>
          <div className="row gx-lg-2 gx-0 gy-0">
            <div className="col-xl-3 col-lg-4">
              <div className="sidebar-data-visualization">
                <div
                  className={`main-button ${
                    dir === "rtl" ? "r-line" : "l-line"
                  }`}
                >
                  <select
                    name={`main-subject`}
                    id="main-subject"
                    onChange={(e) => {
                      setMainId(e.target.value);
                      setFilters({});
                      setDefaultVisualData({});
                      resetChartsData();
                      setSubSubjectId(
                        subSubjectsListWithMainId?.data?.data[0]?.id
                      );
                    }}
                    defaultValue={mainId}
                  >
                    {mainList?.data?.data &&
                      mainList?.data?.data.map((el, i) => (
                        <option
                          key={i}
                          value={el.id}
                          defaultChecked={el.id === mainId}
                          title={LabelLangString({
                            id: "name",
                            currentLocale: currentLocale,
                            defaultValue: el.name,
                            translations: el.translations,
                          })}
                        >
                          {LabelLangString({
                            id: "name",
                            currentLocale: currentLocale,
                            defaultValue: el.name,
                            translations: el.translations,
                          })}
                        </option>
                      ))}
                  </select>
                </div>
                <div
                  className={`second-button ${
                    dir === "rtl" ? "r-line" : "l-line"
                  }`}
                >
                  {subSubjectsListWithMainId?.data?.data &&
                  Object.keys(subSubjectsListWithMainId?.data?.data).length >
                    0 ? (
                    <select
                      name="second-subject"
                      id="second-subject"
                      onChange={(e) => {
                        setSubSubjectId(e.target.value);
                        setIndicatorIdData(1);
                        setFilters({
                          ...defaultState,
                          SubSubjectId: e.target.value,
                        });
                        setDefaultVisualData({});
                        resetChartsData();
                      }}
                      defaultValue={subSubjectId}
                    >
                      {subSubjectsListWithMainId?.data?.data &&
                        subSubjectsListWithMainId?.data?.data.map((el, i) => (
                          <option
                            key={i}
                            value={el.id}
                            defaultChecked={el.id === subSubjectId}
                            title={LabelLangString({
                              id: "title",
                              currentLocale: currentLocale,
                              defaultValue: el.title,
                              translations: el.subjectTranslations,
                            })}
                          >
                            {LabelLangString({
                              id: "title",
                              currentLocale: currentLocale,
                              defaultValue: el.title,
                              translations: el.subjectTranslations,
                            })}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <button>
                      {intl.formatMessage({ id: "data-not-found" })}
                    </button>
                  )}
                </div>
                <form
                  className="radio-box"
                  onChange={(e) => {
                    setIndicatorIdData(e.target.value);
                    setFilters({
                      ...defaultState,
                      IndicatorId: e.target.value,
                    });
                    setDefaultVisualData({
                      ...defaultVisualData,
                      indicatorId: e.target.value,
                    });
                    resetChartsData();
                  }}
                >
                  {indicatorsList.isLoading || indicatorsList.error ? (
                    <div className="text-center" dir="ltr">
                      <h5>{intl.formatMessage({ id: "loading..." })}</h5>
                    </div>
                  ) : Object.keys(renderIndicatorList()).length > 0 ? (
                    <>
                      {renderIndicatorList()
                        .map((el, i) => (
                          <div
                            key={el.indicatorId}
                            className={`item ${
                              dir === "rtl" ? "r-line" : "l-line"
                            }`}
                            ref={(element) =>
                              (indicatorRefs.current[el?.indicatorId] = element)
                            }
                          >
                            <input
                              type="radio"
                              name={`radio-${el.name}-${el.indicatorId}`}
                              id={`radio-${el.name}-${el.indicatorId}`}
                              value={el.indicatorId}
                              checked={+indicatorIdData === el.indicatorId}
                              onChange={() =>
                                setIndicatorIdData(el.indicatorId)
                              }
                            />
                            <label
                              htmlFor={`radio-${el.name}-${el.indicatorId}`}
                              title={LabelLangString({
                                id: "name",
                                currentLocale: currentLocale,
                                defaultValue: el.name,
                                translations: el.indicatorTranslations,
                              })}
                            >
                              {LabelLangString({
                                id: "name",
                                currentLocale: currentLocale,
                                defaultValue: el.name,
                                translations: el.indicatorTranslations,
                              })}
                            </label>
                          </div>
                        ))}
                    </>
                  ) : (
                    <div className={`mt-3 ${dir === "rtl" ? "me-3" : "ms-3"}`}>
                      <DataNotFound />
                    </div>
                  )}
                </form>
              </div>
            </div>
            <div className="col-xl-9 col-lg-8">
              <div className="content-data-visualization">
                <div className="row gx-md-2 gx-0 gy-3 gy-md-0 d-flex justify-content-md-between align-items-md-center">
                  <div className="col-md-8">
                    <div className="title-content">
                      <h5>
                        {getSelectedIndicator() ? (
                          <LabelLang
                            id="name"
                            defaultValue={getSelectedIndicator().name}
                            translations={
                              getSelectedIndicator().indicatorTranslations
                            }
                          />
                        ) : (
                          // <DataNotFound />
                          <h5 className="text-black-50 fw-semibold">
                            [{intl.formatMessage({ id: "select-indicator" })}
                            ...]
                          </h5>
                        )}
                      </h5>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className={`switch-cases`}>
                      <h6 className={`${show ? "active" : ""}`}>
                        <StackedLineChartOutlinedIcon />
                        {intl.formatMessage({ id: "charts" })}
                      </h6>
                      <Switch
                        defaultChecked
                        onChange={(e) => {
                          setShow(e.target.checked);
                        }}
                      />
                      <h6 className={`${!show ? "active" : ""}`}>
                        <TableViewOutlinedIcon />
                        {intl.formatMessage({ id: "tables" })}
                      </h6>
                    </div>
                  </div>
                </div>
                <div className="box-content position-relative">
                  <div
                    className={`show-icon ${dir === "rtl" ? "r-dir" : "l-dir"}`}
                    onClick={handleDirContentFixedBar}
                  >
                    <Button>
                      <ChevronIcon />
                    </Button>
                  </div>
                  <form className="fixed-sidebar-box-content">
                    {indicatorData.isLoading || indicatorData.error ? (
                      <div className="text-center" dir="ltr">
                        <h5>{intl.formatMessage({ id: "loading..." })}</h5>
                      </div>
                    ) : !getSelectedIndicator() ? (
                      <div className="text-center">
                        <h5>
                          {intl.formatMessage({ id: "select-indicator" })}
                        </h5>
                      </div>
                    ) : listOfCheckBox &&
                      Object.keys(listOfCheckBox).length > 0 ? (
                      <>
                        <div className="group-check-box">
                          <input
                            type="checkbox"
                            name={`fixed-checkbox-all`}
                            id={`fixed-checkbox-all`}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIndicators(() => {
                                  return getListOfCheckBox()?.map((item) => {
                                    return {
                                      id: item.id,
                                      checked: true,
                                    };
                                  });
                                });
                              } else {
                                resetChartsData();
                              }
                            }}
                          />
                          <div className="container-label">
                            <label
                              htmlFor={`fixed-checkbox-all`}
                              title={intl.formatMessage({
                                id: "select-all",
                              })}
                            >
                              {intl.formatMessage({
                                id: "select-all",
                              })}
                            </label>
                          </div>
                        </div>
                        {getListOfCheckBox()?.map(
                          ({ id, name, translations }, i) => (
                            <div key={i} className="group-check-box">
                              <input
                                type="checkbox"
                                name={`checkbox-${name}-${id}`}
                                id={`checkbox-${name}-${id}`}
                                checked={getChecked(id)}
                                onChange={(e) => {
                                  handleCheckedChange(id, e.target.checked);
                                }}
                              />
                              <div className="container-label">
                                <label
                                  htmlFor={`checkbox-${name}-${id}`}
                                  title={LabelLangString({
                                    id: "name",
                                    currentLocale: currentLocale,
                                    defaultValue: name,
                                    translations: translations,
                                  })}
                                  className={`${
                                    LabelLangString({
                                      id: "name",
                                      currentLocale: currentLocale,
                                      defaultValue: name,
                                      translations: translations,
                                    }).length > 28
                                      ? "over-28"
                                      : ""
                                  }`}
                                >
                                  {LabelLangString({
                                    id: "name",
                                    currentLocale: currentLocale,
                                    defaultValue: name,
                                    translations: translations,
                                  })}
                                </label>
                              </div>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <DataNotFound />
                    )}
                  </form>
                  <div className="row gx-md-2 gx-0 gy-0">
                    <div className="col-lg-3 col-md-4">
                      <form className="sidebar-box-content">
                        {indicatorData.isLoading || indicatorData.error ? (
                          <div className="text-center" dir="ltr">
                            <h5>{intl.formatMessage({ id: "loading..." })}</h5>
                          </div>
                        ) : !getSelectedIndicator() ? (
                          <div className="text-center">
                            <h5>
                              {intl.formatMessage({ id: "select-indicator" })}
                            </h5>
                          </div>
                        ) : listOfCheckBox &&
                          Object.keys(listOfCheckBox).length > 0 ? (
                          <>
                            <div className="group-check-box">
                              <input
                                type="checkbox"
                                name={`default-checkbox-all`}
                                id={`default-checkbox-all`}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedIndicators(() => {
                                      return getListOfCheckBox()?.map(
                                        (item) => {
                                          return {
                                            id: item.id,
                                            checked: true,
                                          };
                                        }
                                      );
                                    });
                                  } else {
                                    resetChartsData();
                                  }
                                }}
                              />
                              <div className="container-label">
                                <label
                                  htmlFor={`default-checkbox-all`}
                                  title={intl.formatMessage({
                                    id: "select-all",
                                  })}
                                >
                                  {intl.formatMessage({
                                    id: "select-all",
                                  })}
                                </label>
                              </div>
                            </div>
                            {getListOfCheckBox()?.map(
                              ({ id, name, translations }, i) => (
                                <div key={i} className="group-check-box">
                                  <input
                                    type="checkbox"
                                    name={`checkbox-${name}-${id}`}
                                    id={`checkbox-${name}-${id}`}
                                    checked={getChecked(id)}
                                    onChange={(e) => {
                                      handleCheckedChange(id, e.target.checked);
                                    }}
                                  />
                                  <div className="container-label">
                                    <label
                                      htmlFor={`checkbox-${name}-${id}`}
                                      title={LabelLangString({
                                        id: "name",
                                        currentLocale: currentLocale,
                                        defaultValue: name,
                                        translations: translations,
                                      })}
                                      className={`${
                                        LabelLangString({
                                          id: "name",
                                          currentLocale: currentLocale,
                                          defaultValue: name,
                                          translations: translations,
                                        }).length > 28
                                          ? "over-28"
                                          : ""
                                      }`}
                                    >
                                      {LabelLangString({
                                        id: "name",
                                        currentLocale: currentLocale,
                                        defaultValue: name,
                                        translations: translations,
                                      })}
                                    </label>
                                  </div>
                                </div>
                              )
                            )}
                          </>
                        ) : (
                          <DataNotFound />
                        )}
                      </form>
                    </div>
                    <div className="col-lg-9 col-md-8" ref={elementPdf}>
                      <div className="container-data">
                        <div className="charts-box-content">
                          {show ? (
                            <LineData
                              allDataChart={allChartData}
                              years={getColHeader()}
                              indicatorName={LabelLangString({
                                currentLocale: currentLocale,
                                id: "name",
                                defaultValue:
                                  visualizationDetails?.data?.data?.name,
                                translations:
                                  visualizationDetails?.data?.data
                                    ?.indicatorTranslations,
                              })}
                              selectedFilters={selectedIndicators}
                              yearType={
                                visualizationDetails?.data?.data?.yearTypeId
                              }
                            />
                          ) : (
                            <TableData
                              columns={getColHeader()}
                              rows={allChartData}
                              indicatorName={LabelLangString({
                                currentLocale: currentLocale,
                                id: "name",
                                defaultValue:
                                  visualizationDetails?.data?.data?.name,
                                translations:
                                  visualizationDetails?.data?.data
                                    ?.indicatorTranslations,
                              })}
                              yearType={
                                visualizationDetails?.data?.data?.yearTypeId
                              }
                            />
                          )}
                        </div>
                        {visualizationDetails.isLoading ||
                        visualizationDetails.error ? (
                          <div className="text-center" dir="ltr">
                            <h5>{intl.formatMessage({ id: "loading..." })}</h5>
                          </div>
                        ) : (
                          <div className="row gx-md-2 gx-0 gy-2">
                            <div className="col-lg-3 col-md-6">
                              <div className="item-box-content">
                                <h6>
                                  <FormattedMessage id="unit" />:
                                </h6>
                                <span>
                                  {visualizationDetails?.data?.data
                                    ?.measureUnit ? (
                                    <LabelLang
                                      id="name"
                                      defaultValue={
                                        visualizationDetails?.data?.data
                                          ?.measureUnit?.name
                                      }
                                      translations={
                                        visualizationDetails?.data?.data
                                          ?.measureUnit?.measureUnitTranslations
                                      }
                                    />
                                  ) : (
                                    <FormattedMessage id="data-not-found" />
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="item-box-content">
                                <h6>
                                  <FormattedMessage id="periodic" />:
                                </h6>
                                <span>
                                  {visualizationDetails?.data?.data
                                    ?.periodic ? (
                                    <LabelLang
                                      id="name"
                                      defaultValue={
                                        visualizationDetails?.data?.data
                                          ?.periodic?.name
                                      }
                                      translations={
                                        visualizationDetails?.data?.data
                                          ?.periodic?.periodicTranslations
                                      }
                                    />
                                  ) : (
                                    <FormattedMessage id="data-not-found" />
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="item-box-content">
                                <h6>
                                  <FormattedMessage id="start-period" />:
                                </h6>
                                <span>
                                  {getColHeader()[1]?.headerName ? (
                                    getColHeader()[1]?.headerName
                                  ) : (
                                    <FormattedMessage id="data-not-found" />
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="item-box-content">
                                <h6>
                                  <FormattedMessage id="end-period" />:
                                </h6>
                                <span>
                                  {getColHeader()[getColHeader()?.length - 1]
                                    ?.headerName ? (
                                    getColHeader()[getColHeader()?.length - 1]
                                      ?.headerName
                                  ) : (
                                    <FormattedMessage id="data-not-found" />
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="item-box-content">
                                <h6>
                                  <FormattedMessage id="main-source" />:
                                </h6>
                                <span>
                                  {
                                    <Link
                                      to={`/publications/${
                                        getIndicatorPublication(indicatorIdData)
                                          ?.id
                                      }`}
                                    >
                                      {`${intl.formatMessage({
                                        id: "agency",
                                      })} -
                                      ${LabelLangString({
                                        id: "name",
                                        currentLocale: currentLocale,
                                        defaultValue:
                                          getIndicatorPublication(
                                            indicatorIdData
                                          )?.name,
                                        translations:
                                          getIndicatorPublication(
                                            indicatorIdData
                                          )?.publicationTranslations,
                                      })}
                                      `}
                                    </Link>
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="item-box-content mb-4">
                                <h6>
                                  <FormattedMessage id="statistical-notes" />:
                                </h6>
                                <span>
                                  {visualizationDetails?.data?.data?.notes ? (
                                    <HtmlView
                                      html={LabelLangString({
                                        id: "notes",
                                        currentLocale: currentLocale,
                                        defaultValue:
                                          visualizationDetails?.data?.data
                                            ?.notes,
                                        translations:
                                          visualizationDetails?.data?.data
                                            ?.indicatorTranslations,
                                      })}
                                    />
                                  ) : (
                                    <FormattedMessage id="data-not-found" />
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row gx-md-4 gx-0 gy-md-0 gy-3 my-3">
                  <div className="col-md-4">
                    <div className="item-show-data">
                      <div className="title">
                        <h5>
                          <FormattedMessage id="download-chart-table" />:
                        </h5>
                      </div>
                      <div className="content">
                        {!show ? (
                          <TableExportActions
                            className={dir === "rtl" ? "ms-1" : "me-1"}
                            columns={getColHeader()}
                            rows={allChartData}
                            yearType={
                              visualizationDetails?.data?.data?.yearTypeId
                            }
                            currentLocale={currentLocale}
                            filename={tableExportFilename}
                            tableId="table-to-xls"
                            excelImage={excelImage}
                            metadata={{
                              title: tableExportTitle,
                              locale: currentLocale,
                            }}
                          />
                        ) : (
                          ""
                        )}
                        {show ? (
                          <ChartExportActions
                            className={dir === "rtl" ? "ms-1" : "me-1"}
                            hasData={hasExportableData}
                            onExportPdf={handleChartPdfExport}
                            onExportPng={handleChartPngExport}
                            onExportJpeg={handleChartJpegExport}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContentLoading>
  );
}

export default DataVisualization;
